import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DefinitionTooltip,
  type DefinitionTooltipInfo,
} from "src/components/common";
import {
  GoogleGroupedBarLegend,
  GROUPED_BAR_COLORS,
  METRIC_COLORS,
  OrganicChartTooltip,
  truncateChartLabel,
  type GoogleBarMetricKey,
  type GoogleGroupedChartDatum,
} from "./GoogleAdsChartConfig";
import type { GoogleSectionHelpKey } from "./GoogleAdsTableConfig";
import { formatPaidMediaPercent, formatPaidMediaRoas } from "../../utils/PaidMediaNumberFormat";
import { usePaidMediaChartRtl } from "../../utils/PaidMediaChartRtl";

interface GoogleChartsRow1Props {
  googleSectionHelp: (_key: GoogleSectionHelpKey) => DefinitionTooltipInfo;
  roasChartData: Array<{ name: string; roas: number }>;
  adGroupChartData: GoogleGroupedChartDatum[];
  adGroupSpendPieData: GoogleGroupedChartDatum[];
  clicksConversionsLegendItems: ReadonlyArray<{ key: GoogleBarMetricKey; label: string }>;
  spendLegendItems: ReadonlyArray<{ key: GoogleBarMetricKey; label: string }>;
  barSeriesLabels: { spend: string; impressions: string; clicks: string; conversions: string; ctr: string };
  formatChartAxisCompactLocalized: (_value: unknown) => string;
  formatCountMetric: (_value: number | null | undefined) => string;
  formatConversionMetric: (_value: number | null | undefined) => string;
  formatCurrency: (
    _value: number,
    _options?: { minimumFractionDigits?: number; maximumFractionDigits?: number }
  ) => string;
  numberLocale: string;
}

const GoogleChartsRow1: FC<GoogleChartsRow1Props> = ({
  googleSectionHelp,
  roasChartData,
  adGroupChartData,
  adGroupSpendPieData,
  clicksConversionsLegendItems,
  spendLegendItems,
  barSeriesLabels,
  formatChartAxisCompactLocalized,
  formatCountMetric,
  formatConversionMetric,
  formatCurrency,
  numberLocale,
}) => {
  const { t } = useTranslation();
  const chartRtl = usePaidMediaChartRtl();

  return (
    <div className="paid-media-google-charts-row1" data-pdf-row="google-charts-primary">
      <article className="paid-media-panel paid-media-panel--chart-stack" data-pdf-block="roas-by-campaign">
        <div className="paid-media-panel__header">
          <div className="paid-media-panel__heading">
            <h3>{t("googleAdsReport.sections.roasByCampaign")}</h3>
            <DefinitionTooltip
              label={t("googleAdsReport.sections.roasByCampaign")}
              info={googleSectionHelp("roasByCampaign")}
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
          </div>
        </div>
        <div className="paid-media-chart paid-media-chart--roas-stack">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={roasChartData}
              barCategoryGap="16%"
              margin={chartRtl.margin({ top: 24, right: 6, bottom: 8, left: -18 })}
            >
              <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                interval={0}
                reversed={chartRtl.axisReversed}
                tickFormatter={value => truncateChartLabel(value, 12)}
                tick={{ fontSize: 10, fill: "#7b8794" }}
                angle={-35}
                textAnchor="end"
                height={54}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                orientation={chartRtl.yAxisOrientation}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatChartAxisCompactLocalized}
              />
              <Tooltip
                content={({ active, label, payload }) => (
                  <OrganicChartTooltip
                    active={active}
                    label={label}
                    labelTitle={t("googleAdsReport.chart.campaign")}
                    payload={payload}
                    seriesLabelForDataKey={() => t("googleAdsReport.kpis.roas")}
                    valueFormatter={value => formatPaidMediaRoas(Number(value ?? 0))}
                  />
                )}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar
                dataKey="roas"
                fill={METRIC_COLORS.spend}
                maxBarSize={42}
                radius={[5, 5, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>

      {/* Ad Group Performance — clicks + conversions */}
      <article className="paid-media-panel paid-media-panel--chart-stack" data-pdf-block="ad-group-performance">
        <div className="paid-media-panel__header paid-media-panel__header--with-legend">
          <div className="paid-media-panel__heading">
            <h3>{t("googleAdsReport.sections.adGroupPerformance")}</h3>
            <DefinitionTooltip
              label={t("googleAdsReport.sections.adGroupPerformance")}
              info={googleSectionHelp("adGroupPerformance")}
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
          </div>
          <GoogleGroupedBarLegend items={clicksConversionsLegendItems} />
        </div>
        <div className="paid-media-chart paid-media-chart--grouped">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={adGroupChartData}
              barGap={4}
              barCategoryGap={24}
              margin={chartRtl.margin({ top: 6, right: 10, bottom: 8, left: 0 })}
              maxBarSize={15}
            >
              <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                reversed={chartRtl.axisReversed}
                tickFormatter={value => truncateChartLabel(value, 12)}
                tick={{ fontSize: 10, fill: "#60718b" }}
                angle={-35}
                textAnchor="end"
                height={54}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={34}
                orientation={chartRtl.yAxisOrientation}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={value => formatPaidMediaPercent(Number(value), 0)}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  // Ad group names can repeat (same name, different IDs), so read
                  // the hovered row from the payload instead of matching by name.
                  const row = payload?.[0]?.payload as GoogleGroupedChartDatum | undefined;
                  return (
                    <OrganicChartTooltip
                      active={active}
                      label={label}
                      labelTitle={t("googleAdsReport.chart.adGroup")}
                      payload={payload}
                      seriesLabelForDataKey={key => {
                        if (key === "clicksValue") {
                          return barSeriesLabels.clicks;
                        }
                        if (key === "conversionsValue") {
                          return barSeriesLabels.conversions;
                        }
                        return key;
                      }}
                      valueFormatter={(value, name) => {
                        if (name === "clicksValue") {
                          return formatCountMetric(row?.clicks ?? Number(value ?? 0));
                        }
                        if (name === "conversionsValue") {
                          return formatConversionMetric(row?.conversions ?? Number(value ?? 0));
                        }
                        return Number(value ?? 0).toLocaleString(numberLocale);
                      }}
                    />
                  );
                }}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="clicksValue" fill={GROUPED_BAR_COLORS.clicks} barSize={28} radius={[5, 5, 0, 0]} name="clicksValue" />
              <Bar dataKey="conversionsValue" fill={GROUPED_BAR_COLORS.conversions} barSize={28} radius={[5, 5, 0, 0]} name="conversionsValue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>

      {/* Spend by Ad Group — spend only */}
      <article className="paid-media-panel paid-media-panel--chart-stack" data-pdf-block="spend-by-ad-group">
        <div className="paid-media-panel__header paid-media-panel__header--with-legend">
          <div className="paid-media-panel__heading">
            <h3>{t("googleAdsReport.sections.spendByAdGroup")}</h3>
            <DefinitionTooltip
              label={t("googleAdsReport.sections.spendByAdGroup")}
              info={googleSectionHelp("spendByAdGroup")}
              formulaTitle={t("webAnalytics.kpi.formula")}
            />
          </div>
          <GoogleGroupedBarLegend items={spendLegendItems} />
        </div>
        <div className="paid-media-chart paid-media-chart--grouped">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={adGroupSpendPieData}
              barGap={4}
              barCategoryGap="20%"
              margin={chartRtl.margin({ top: 6, right: 10, bottom: 8, left: 0 })}
              maxBarSize={28}
            >
              <CartesianGrid stroke="#e8edf4" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                interval={0}
                reversed={chartRtl.axisReversed}
                tickFormatter={value => truncateChartLabel(value, 12)}
                tick={{ fontSize: 10, fill: "#60718b" }}
                angle={-35}
                textAnchor="end"
                height={54}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                width={34}
                orientation={chartRtl.yAxisOrientation}
                tick={{ fontSize: 11, fill: "#60718b" }}
                axisLine={false}
                tickLine={false}
                domain={[0, 100]}
                tickFormatter={value => formatPaidMediaPercent(Number(value), 0)}
              />
              <Tooltip
                content={({ active, label, payload }) => {
                  // Ad group names can repeat (same name, different IDs), so read
                  // the hovered row from the payload instead of matching by name.
                  const row = payload?.[0]?.payload as GoogleGroupedChartDatum | undefined;
                  return (
                    <OrganicChartTooltip
                      active={active}
                      label={label}
                      labelTitle={t("googleAdsReport.chart.adGroup")}
                      payload={payload}
                      seriesLabelForDataKey={() => barSeriesLabels.spend}
                      valueFormatter={(value, name) => {
                        if (name === "spendValue") {
                          return formatCurrency(row?.spend ?? Number(value ?? 0));
                        }
                        return Number(value ?? 0).toLocaleString(numberLocale);
                      }}
                    />
                  );
                }}
                wrapperStyle={{ pointerEvents: "none" }}
              />
              <Bar dataKey="spendValue" fill={GROUPED_BAR_COLORS.spend} barSize={28} radius={[5, 5, 0, 0]} name="spendValue" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </article>
    </div>
  );
};

export default GoogleChartsRow1;
